'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { changePassword } from '@eliadmin/core/src/api/customers/customers-api';
import { PasswordField } from '@eliadmin/core/src/components/nt/account/password/password-field';
import {
  PASSWORD_RULES_ISSUE,
  passwordSchema,
  type PasswordFormValues,
} from '@eliadmin/core/src/components/nt/account/password/password-schema';
import {
  PasswordRules,
  PasswordStrength,
} from '@eliadmin/core/src/components/nt/account/password/password-strength';
import { AccountCard } from '@eliadmin/core/src/components/nt/account/shell/account-card';
import {
  FormResultMessage,
  type FormResult,
} from '@eliadmin/core/src/components/nt/account/shell/form-result';
import { applyServerErrors } from '@eliadmin/core/src/components/nt/account/shell/server-errors';
import { useAccountTexts } from '@eliadmin/core/src/components/nt/shared/site-texts';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

const EMPTY: PasswordFormValues = { password_old: '', password: '', password_confirmation: '' };
const FIELDS = Object.keys(EMPTY) as (keyof PasswordFormValues)[];
const RULES_ID = 'password-rules';

/**
 * Figma „Card · Nové heslo“ (207:11374): current password, the new one with the strength meter and
 * the admin rules, the repeat with its success state once both match, „Zmeniť heslo“. The designed
 * „Odhlásiť ma zo všetkých ostatných zariadení“ is left out – the core change-password request
 * (`password_old`, `password`, `password_confirmation`) has no such option. A 422 goes onto the
 * fields it names; a success empties the form and is announced next to the button until the next
 * edit.
 */
export function PasswordForm({ email, authToken }: { email: string; authToken: string }) {
  const { t } = useTranslations();
  const texts = useAccountTexts();
  const locale = useLocaleStore(s => s.currentLocale);
  const { formValidationMessages } = useFormValidationMessages(t);
  const [result, setResult] = useState<FormResult>(null);
  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(
      passwordSchema(formValidationMessages, texts.passwordRules, t('text1103')),
    ),
    mode: 'onTouched',
    defaultValues: EMPTY,
  });
  const [password, confirmation] = useWatch({
    control: form.control,
    name: ['password', 'password_confirmation'],
  });
  const { isDirty, isSubmitting, errors } = form.formState;
  const matches = !!confirmation && confirmation === password && !errors.password_confirmation;

  // A changed new password re-checks a repeat the customer already went through.
  const { getFieldState, trigger } = form;
  useEffect(() => {
    if (getFieldState('password_confirmation').isTouched) void trigger('password_confirmation');
  }, [password, getFieldState, trigger]);

  const onSubmit = form.handleSubmit(async values => {
    setResult(null);
    try {
      await changePassword({ ...values, authToken, locale });
      form.reset(EMPTY);
      setResult({ status: 'success', message: t('text649') });
    } catch (error) {
      const rest = applyServerErrors(error, form.setError, field =>
        FIELDS.find(name => name === field),
      );
      if (rest) setResult({ status: 'failed', message: rest.join('\n') || t('text650') });
    }
  });

  return (
    <AccountCard title={t('text203')}>
      <FormProvider {...form}>
        <form noValidate onSubmit={onSubmit} className="flex flex-col gap-4">
          {/* Lets password managers file the new password under the account. */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={email}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className="sr-only"
          />
          <PasswordField
            name="password_old"
            label={t('text3415')}
            autoComplete="current-password"
          />
          <PasswordField
            name="password"
            label={t('text203')}
            autoComplete="new-password"
            describes={RULES_ID}
          />
          <PasswordStrength password={password} />
          <PasswordRules
            id={RULES_ID}
            password={password}
            showErrors={errors.password?.message === PASSWORD_RULES_ISSUE}
          />
          <PasswordField
            name="password_confirmation"
            label={texts.repeatPassword}
            autoComplete="new-password"
            success={matches}
            hint={
              matches && texts.passwordsMatch ? (
                <span className="text-[#0E6A20]">{texts.passwordsMatch}</span>
              ) : undefined
            }
          />
          <div className="flex flex-col gap-4 @[30rem]:flex-row @[30rem]:items-center">
            <Button
              type="submit"
              iconLeft="lock-simple"
              isLoading={isSubmitting}
              className="w-full @[30rem]:w-auto"
            >
              {t('text187')}
            </Button>
            <FormResultMessage
              result={result?.status === 'success' && isDirty ? null : result}
              className="empty:-mt-4 @[30rem]:flex-1 @[30rem]:empty:mt-0"
            />
          </div>
        </form>
      </FormProvider>
    </AccountCard>
  );
}
